import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../button/button.component';
import { MODAL_REF } from './modal.service';

@Component({
  selector: 'ui-modal',
  standalone: true,
  imports: [CommonModule, ButtonComponent],
  template: `
    <!-- Backdrop -->
    <div
      class="modal-backdrop"
      [style.zIndex]="modalRef.zIndex - 1"
      (click)="onBackdropClick($event)"
    ></div>

    <!-- Modal content -->
    <div
      class="modal-content bg-white rounded-lg shadow-xl max-w-2xl w-full mt-10"
      [style.zIndex]="modalRef.zIndex"
      [class]="getModalSizeClass()"
    >
      <!-- Header -->
      <div
        class="px-4 py-3 border-b border-slate-200 flex justify-between items-center rounded-t-lg"
        [class]="variant === 'danger' ? 'bg-danger-50' : 'bg-primary-25'"
      >
        <h2
          class="text-lg font-medium"
          [class]="variant === 'danger' ? 'text-red-600' : 'text-primary-600'"
        >
          {{ title }}
        </h2>
        @if(!hideClose) {
        <ui-button
          [variant]="'ghost'"
          icon="close"
          [hideLabel]="true"
          (onClick)="close.emit()"
          >
            Sluiten
          </ui-button>
        }
      </div>

      <!-- Content -->
      <div [class]="getContentClasses()">
        <ng-content></ng-content>
      </div>

      <!-- Footer -->
      <div
        class="px-4 py-3 border-t border-slate-200 flex justify-between gap-2"
      >
        <div>
          <div *ngFor="let button of buttonsLeft">
            <ui-button
              [variant]="button.variant"
              (onClick)="button.onClick()"
              [disabled]="button.disabled ?? false"
              [icon]="button.icon"
            >
              {{ button.text }}
            </ui-button>
          </div>
        </div>
        <div class="flex gap-2">
          @if(!hideCancel) {
          <ui-button variant="secondary" (onClick)="close.emit()">
            {{ cancelText }}
          </ui-button>
          }
          @if(!hideSubmit) {
          <ui-button
            [variant]="variant === 'danger' ? 'danger' : 'primary'"
            [disabled]="disabled"
            [loading]="loading"
            (onClick)="save.emit()"
          >
              {{ submitText }}
            </ui-button>
          }
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .modal-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 2000;
      }

      .modal-content {
        position: absolute;
        top: 5%;
        left: 50%;
        max-height: 85%;
        transform: translate(-50%, 0);
        margin: 1.5rem;
        width: 100%;
        overflow-y: hidden;
        display: flex;
        flex-direction: column;
      }

      .modal-content.size-sm {
        max-width: 24rem;
      }

      .modal-content.size-md {
        max-width: 32rem;
      }

      .modal-content.size-lg {
        max-width: 42rem;
      }

      .modal-content.size-xl {
        max-width: 56rem;
      }

      .modal-content.size-full {
        max-width: calc(100% - 3rem);
      }
    `,
  ],
})
export class ModalComponent {
  @Input() title: string = '';
  @Input() submitText: string = 'Opslaan';
  @Input() hideSubmit: boolean = false;
  @Input() cancelText: string = 'Annuleren';
  @Input() hideCancel: boolean = false;
  @Input() hideClose: boolean = false;
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' | 'full' = 'md';
  @Input() variant: 'primary' | 'danger' | 'success' | 'warning' = 'primary';
  @Input() buttonsLeft: {
    text: string;
    variant: 'primary' | 'danger' | 'secondary' | 'ghost';
    onClick: () => void;
    disabled?: boolean;
    icon?: string;
  }[] = [];
  @Input() closeOnBackdrop = true;

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();

  modalRef = inject(MODAL_REF);

  getContentClasses(): string {
    return `p-4 ${
      this.size === 'full' ? 'overflow-auto h-[calc(100%-129px)]' : 'overflow-auto h-[calc(100%-129px)]'
    }`;
  }

  getModalSizeClass(): string {
    return `size-${this.size}`;
  }

  onBackdropClick(event: MouseEvent): void {
    if (this.closeOnBackdrop) {
      this.close.emit();
    }
  }
}
